import React, { useMemo, useState } from 'react'
import Accordion from 'react-bootstrap/Accordion'
import { FiAlertCircle } from 'react-icons/fi'
import { Spinner } from 'react-bootstrap'
import AccordionHeaderContent from './AccordionHeaderContent'
import { ReservationDonorCard } from '@components/Cards/DonorCard'
import ClinicInfoCards from '@components/InfoCards/ClinicInfoCards'
import PatientInfoCards from '@components/InfoCards/PatientInfoCards'
import ImageLightbox from '@components/ImageLightbox/ImageLightbox'
import IDonor from '@interfaces/IDonor'
import ICustomImportDonor from '@interfaces/ICustomImportDonor'
import IOrder from '@interfaces/IOrder'
import IUser from '@interfaces/IUser'
import OrderTypeEnum from '@enums/OrderTypeEnum'
import { getStepState } from '../utils/getStepState'
import { FaInfoCircle } from 'react-icons/fa'

interface AccordionReservationProps {
  stepProgress: number
  inProgressOrder: IOrder | null
  clinicCity: string
  clinicState: string
  formattedTreatmentDate: string
  user: IUser | null
  userPhone: string
  donors: IDonor[]
  isLoadingDonors: boolean
  eligibleBankIds: string[]
  onUpdateDoctor?: (doctorName: string, doctorCrm: string) => Promise<void>
}

const AccordionReservation: React.FC<AccordionReservationProps> = ({
  stepProgress,
  inProgressOrder,
  clinicCity,
  clinicState,
  formattedTreatmentDate,
  user,
  userPhone,
  donors,
  isLoadingDonors,
  eligibleBankIds,
  onUpdateDoctor
}) => {
  const stepState = getStepState(stepProgress)

  const [lightboxImage, setLightboxImage] = useState<string | null>(null)

  // Get inProgressOrder donor_ids by the fields: order.first_donor_id, order.second_donor_id, order.third_donor_id, order.fourth_donor_id, order.fifth_donor_id, order.sixth_donor_id
  // After getting the donor_ids, filter the donors array to get the selected donors for the order
  const selectedDonorIds = useMemo(() => {
    if (!inProgressOrder) return []
    const ids = [
      inProgressOrder.first_donor_id,
      inProgressOrder.second_donor_id,
      inProgressOrder.third_donor_id,
      inProgressOrder.fourth_donor_id,
      inProgressOrder.fifth_donor_id,
      inProgressOrder.sixth_donor_id
    ]
    return ids.filter(id => id !== null && id !== undefined) as number[]
  }, [inProgressOrder])

  const selectedDonors = useMemo(() => {
    return donors.filter(donor => selectedDonorIds.includes(donor.id))
  }, [donors, selectedDonorIds])

  const isCustomImport = inProgressOrder?.order_type === OrderTypeEnum.CUSTOM_IMPORT

  // Custom import orders keep their donors in dedicated slots, rendered with the same rich card
  const customImportDonors = useMemo(() => {
    if (!inProgressOrder) return []
    return [
      inProgressOrder.first_custom_import_donor,
      inProgressOrder.second_custom_import_donor,
      inProgressOrder.third_custom_import_donor
    ].filter((donor): donor is ICustomImportDonor => donor !== null)
  }, [inProgressOrder])

  const displayDonors = isCustomImport ? customImportDonors : selectedDonors

  const handleOpenLightbox = (imageUrl: string) => setLightboxImage(imageUrl)
  const handleCloseLightbox = () => setLightboxImage(null)

  // Calculate Photomatching availability based on the same rules as CheckoutStep
  const hasAtLeastThreeDonors = useMemo(() => selectedDonors.length >= 3, [selectedDonors])

  const hasAtLeastThreeWithSameBank = useMemo(() => {
    const spermBankCounts: Record<string, number> = {}
    selectedDonors.forEach(donor => {
      const id = donor.sperm_bank_id
      if (id && String(id).trim()) {
        const bankId = String(id)
        spermBankCounts[bankId] = (spermBankCounts[bankId] || 0) + 1
      }
    })
    return Object.values(spermBankCounts).some(count => count >= 3)
  }, [selectedDonors])

  const isPhotoMatchAvailable = useMemo(() => {
    return hasAtLeastThreeDonors && hasAtLeastThreeWithSameBank
  }, [hasAtLeastThreeDonors, hasAtLeastThreeWithSameBank])

  const photoMatchEligibleDonors = useMemo(() => {
    if (!isPhotoMatchAvailable) return []

    const spermBankCounts: Record<string, number> = {}
    selectedDonors.forEach(d => {
      const id = d.sperm_bank_id
      if (id && String(id).trim()) {
        const bankId = String(id)
        spermBankCounts[bankId] = (spermBankCounts[bankId] || 0) + 1
      }
    })
    const eligibleBankIds = Object.keys(spermBankCounts).filter(id => spermBankCounts[id] >= 3)
    return selectedDonors.filter(d => d.sperm_bank_id && eligibleBankIds.includes(String(d.sperm_bank_id)))
  }, [selectedDonors, isPhotoMatchAvailable])

  return (
    <Accordion.Item eventKey="0" className={`${stepState}-state`}>
      <Accordion.Header>
        <AccordionHeaderContent
          stepState={stepState}
          progress={stepProgress}
          title={(
            <>
              Solicitação de Reserva{inProgressOrder?.photo_match ? (<> + <span style={{ color: '#FA5A84' }}>Photomatching</span></>) : ''}
            </>
          )}
        />
      </Accordion.Header>

      <Accordion.Body>
        <div className="request-summer-page">
          <div className="check-sample px-1 px-lg-3 py-3">
            {/* Clinic Information */}
            <div className="check check-clinic">
              <div className="summary">
                <ClinicInfoCards
                  clinicName={inProgressOrder?.clinic?.trading_name || ''}
                  clinicCity={clinicCity || ''}
                  clinicState={clinicState || ''}
                  doctorName={inProgressOrder?.doctor_name || ''}
                  doctorCrm={inProgressOrder?.doctor_crm || ''}
                  treatmentType={inProgressOrder?.treatment_type || ''}
                  treatmentDate={formattedTreatmentDate}
                  onEditDoctor={onUpdateDoctor}
                />
              </div>
            </div>

            {/* Section Divider */}
            <div className="section-divider">
              <div className="section-divider__line" />
              <span className="section-divider__text">Informações da Paciente</span>
              <div className="section-divider__line" />
            </div>

            {/* Patient Information */}
            <div className="check check-patient">
              <div className="summary">
                <PatientInfoCards user={user} userPhone={userPhone} />
              </div>
            </div>

            {/* Section Divider */}
            <div className="section-divider">
              <div className="section-divider__line" />
              <span className="section-divider__text">Doadores Selecionados</span>
              <div className="section-divider__line" />
            </div>

            {/* Donors Section */}
            <div className="check check-donors">
              <div className="summary">
                {isLoadingDonors ? (
                  <div className="loading-state">
                    <Spinner animation="border" variant="primary" />
                    <p>Carregando doadores...</p>
                  </div>
                ) : displayDonors.length === 0 ? (
                  <div className="empty-state">
                    <FiAlertCircle size={48} />
                    <p>Nenhum doador encontrado</p>
                    <small>Adicione doadores para continuar com o pedido.</small>
                  </div>
                ) : (
                  <div className="card-index">
                    {displayDonors.map((donor, index) => (
                      <ReservationDonorCard
                        key={donor.internal_id}
                        donor={donor}
                        position={index + 1}
                        selectedTreatment={inProgressOrder?.treatment_type}
                        isLast={index === displayDonors.length - 1}
                        showPhotoMatchColumn={!isCustomImport && isPhotoMatchAvailable && Boolean(inProgressOrder?.photo_match)}
                        isPhotoMatchEligible={donor.sperm_bank_id ? eligibleBankIds.includes(String(donor.sperm_bank_id)) : false}
                        disableLink={isCustomImport}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Photomatching Section */}
            {inProgressOrder?.photomatches && inProgressOrder?.photomatches.length > 0 ? (
              <>
                {/* Section Divider */}
                <div className="section-divider mt-4">
                  <div className="section-divider__line" />
                  <span className="section-divider__text">Photomatching</span>
                  <div className="section-divider__line" />
                </div>

                <div className="check check-photomatch">
                  <div className="summary">
                    <div className="row">
                      <div className="col-12">
                        <div className="service">
                          <div className="service-box">

                            {/* Display uploaded images */}
                            {inProgressOrder.photomatches && inProgressOrder.photomatches.length > 0 && (
                              <div className="w-100">
                                <div className="d-flex align-items-center justify-content-center justify-content-lg-start flex-wrap gap-10">
                                  {inProgressOrder.photomatches.map((photomatch) => (
                                    <div key={photomatch.id}>
                                      <img
                                        src={photomatch.file_url}
                                        alt="Foto de referência"
                                        onClick={() => handleOpenLightbox(photomatch.file_url)}
                                        style={{
                                          width: '130px',
                                          height: '130px',
                                          objectFit: 'cover',
                                          borderRadius: '8px',
                                          cursor: 'pointer'
                                        }}
                                      />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Display patient observation */}
                            {inProgressOrder.photomatch_patient_observation && (
                              <div className="w-100 mt-3">
                                <div className="text-area">
                                  <textarea
                                    className="form-control no-resize"
                                    placeholder="Escreva aqui detalhes e observações..."
                                    rows={4}
                                    value={inProgressOrder.photomatch_patient_observation}
                                    readOnly
                                    style={{ backgroundColor: '#f8f9fa' }}
                                  ></textarea>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Photomatching Results Info */}
                          <div className="box payment p-2 pr-3 p-lg-4 mt-3 w-100 mb-0 shadow-sm border-0" style={{
                            borderRadius: '12px',
                            background: 'linear-gradient(135deg, #FA5A84 0%, #FF8BA7 100%)',
                            color: '#fff'
                          }}
                          >
                            <div className="d-flex align-items-center justify-content-between flex-wrap">
                              <div className="text flex-grow-1 mb-3 mb-md-0">
                                <div className="d-flex align-items-start">
                                  <div className="d-flex align-items-center justify-content-center mr-2 mr-lg-4" style={{
                                    width: '42px',
                                    height: '42px',
                                    borderRadius: '10px',
                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                    flexShrink: 0
                                  }}
                                  >
                                    <FaInfoCircle size={20} className="text-white" />
                                  </div>
                                  <div className="text flex-grow-1">
                                    <h5 className="mb-0 font-weight-bold text-white">Resultados do Photomatching</h5>
                                    <p className="mb-0 text-white" style={{ opacity: 0.95, lineHeight: '1.5' }}>
                                      As tratativas e resultados do Photomatching serão informados por E-mail / WhatsApp.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                      </div>

                    </div>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </Accordion.Body>
      <ImageLightbox
        isOpen={!!lightboxImage}
        imageUrl={lightboxImage || ''}
        onClose={handleCloseLightbox}
      />
    </Accordion.Item>
  )
}

export default AccordionReservation
